import React from 'react';
import { useStore, WikiDoc } from '../../store/useStore';
import { RichTextEditor, DocumentUpload, Tabs, Repeater } from '../ui';
import { Brain, FileCode2, MessageSquareText } from 'lucide-react';
import { KnowledgeChatDashboard } from '../KnowledgeChatDashboard';

/**
 * `clienteId` desce por prop até o chat.
 *
 * Ele é o `:id` de `ia.gerarParaCliente`, e essa rota é a única alcançável daqui:
 * este módulo é uma aba do `ClientDashboard`, que roda com sessão de PORTAL.
 * `ia.gerar` é `acesso: 'operador'` e daria 403 para o próprio cliente.
 */
export function M18_KnowledgeBase({ clienteId }: { clienteId: string }) {
  const { knowledgeBase, setKnowledgeBase } = useStore();

  const handleAddWikiDoc = () => {
    const novelDoc: WikiDoc = { id: crypto.randomUUID(), titulo: '', conteudo: '' };
    setKnowledgeBase({ wikiDocs: [...knowledgeBase.wikiDocs, novelDoc] });
  };

  const handleUpdateWikiDoc = (index: number, key: keyof WikiDoc, value: string) => {
    const updated = [...knowledgeBase.wikiDocs];
    updated[index] = { ...updated[index], [key]: value };
    setKnowledgeBase({ wikiDocs: updated });
  };

  const handleRemoveWikiDoc = (index: number) => {
    const updated = knowledgeBase.wikiDocs.filter((_, i) => i !== index);
    setKnowledgeBase({ wikiDocs: updated });
  };

  const tabs = [
    {
      id: 'editor',
      label: 'Editor de Documentação (Wiki)',
      icon: <FileCode2 size={16} />,
      content: (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full p-6">
           <Repeater<WikiDoc>
               title="Documentos da Base"
               description="Crie manuais, regras e políticas para treinar a Inteligência Artificial."
               addButtonText="Criar Documento"
               items={knowledgeBase.wikiDocs}
               onAdd={handleAddWikiDoc}
               onRemove={handleRemoveWikiDoc}
               getItemSummary={(doc) => ({ title: doc.titulo || 'Novo Documento', subtitle: doc.conteudo.replace(/<[^>]*>?/gm, '').substring(0, 50) + '...' })}
               renderItem={(doc, index) => (
                 <div className="space-y-6">
                   <div className="border-b border-gray-100 pb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Título do Documento</label>
                      <input 
                         type="text" 
                         value={doc.titulo}
                         onChange={(e) => handleUpdateWikiDoc(index, 'titulo', e.target.value)}
                         placeholder="Ex: Política de Troca e Devolução"
                         className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                      />
                   </div>
                   <RichTextEditor 
                      label="Conteúdo (Wiki)" 
                      value={doc.conteudo}
                      onChange={(val) => handleUpdateWikiDoc(index, 'conteudo', val)}
                      placeholder="Escreva o conteúdo aqui..."
                      minHeight="min-h-[400px]"
                   />
                 </div>
               )}
           />
        </div>
      )
    },
    {
      id: 'pdf',
      label: 'Upload de Documentos / Manuais (IA)',
      icon: <Brain size={16} />,
      content: (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-6">
           <DocumentUpload 
              label="Base de Dados (PDFs, PPTs) para Embeddings"
              description="A IA vai ler e interpretar tudo. Se você já tem manuais extensos em PDF, apostilas ou guias que não precisam ser recopiados no editor, adicione aqui o quanto for necessário."
              maxFiles={50}
              onChange={(files) => setKnowledgeBase({ documentos: files as any[] })}
           />
        </div>
      )

    },
    {
      id: 'chat',
      label: 'Consultar IA (Chat Docs)',
      icon: <MessageSquareText size={16} />,
      content: (
        <div className="bg-[#121212] rounded-xl shadow-sm border border-gray-800 overflow-hidden h-[800px]">
           <KnowledgeChatDashboard clienteId={clienteId} />
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">Central de Conhecimento Máxima</h2>
        <p className="text-gray-500 mt-1">
          Onde a inteligência molda o agente. Crie documentações estilo Notion ou insira bases de conhecimento massivas.
        </p>
      </div>

      <Tabs tabs={tabs} />
    </div>
  );
}
